// src/components/analytics/utils/dateUtils.js

import { parse, format, addDays, differenceInDays, isValid } from 'date-fns';

/**
 * Common date formats to try when parsing
 */
const DATE_FORMATS = [
  'yyyy-MM-dd',
  'dd/MM/yyyy',
  'MM/dd/yyyy',
  'yyyy/MM/dd',
  'dd-MM-yyyy',
  'MM-dd-yyyy',
  'yyyy-MM-dd HH:mm:ss',
  'dd/MM/yyyy HH:mm:ss',
  'MM/dd/yyyy HH:mm:ss',
  'yyyy-MM',
  'MM/yyyy',
  'MMM yyyy',
  'MMMM yyyy',
  'yyyy',
  'dd MMM yyyy',
  'dd MMMM yyyy'
];

/**
 * Parses a date string trying multiple formats
 * @param {string|Date} dateString - The date string to parse
 * @returns {Date|null} - Parsed date or null if parsing fails
 */
export function parseDate(dateString) {
  // If already a Date object, return it
  if (dateString instanceof Date) {
    return isValid(dateString) ? dateString : null;
  }

  // Handle null/undefined
  if (!dateString) {
    return null;
  }

  // Convert to string if needed
  const dateStr = String(dateString).trim();

  // Try parsing as ISO date first (fastest)
  const isoDate = new Date(dateStr);
  if (isValid(isoDate) && !isNaN(isoDate.getTime())) {
    return isoDate;
  }

  // Try each format
  for (const dateFormat of DATE_FORMATS) {
    try {
      const parsed = parse(dateStr, dateFormat, new Date());
      if (isValid(parsed)) {
        return parsed;
      }
    } catch (e) {
      // Continue to next format
    }
  }

  // Special handling for "YYYY-MM" format (convert to first day of month)
  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    const [year, month] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    if (isValid(date)) {
      return date;
    }
  }

  return null;
}

/**
 * Formats a date to match the original format pattern
 * @param {Date} date - The date to format
 * @param {string} originalFormat - The original format pattern or example
 * @returns {string} - Formatted date string
 */
export function formatDate(date, originalFormat) {
  if (!date || !isValid(date)) {
    return '';
  }

  // If originalFormat is an actual date string, detect its format
  const detectedFormat = detectDateFormat(originalFormat);
  
  if (detectedFormat) {
    try {
      // For month-only formats, ensure we don't add day information
      if (detectedFormat === 'yyyy-MM' && originalFormat && originalFormat.match(/^\d{4}-\d{2}$/)) {
        return format(date, 'yyyy-MM');
      }
      // For any other detected format, use it as-is
      return format(date, detectedFormat);
    } catch (e) {
      console.warn('Failed to format date with detected format:', e);
      // Fall back to trying to match the structure of originalFormat
      if (originalFormat && typeof originalFormat === 'string') {
        // Try to match the general structure (e.g., if original has no day, don't add day)
        if (originalFormat.match(/^\d{4}-\d{2}$/)) return format(date, 'yyyy-MM');
        if (originalFormat.match(/^\d{2}\/\d{4}$/)) return format(date, 'MM/yyyy');
        if (originalFormat.match(/^\d{4}$/)) return format(date, 'yyyy');
      }
    }
  }

  // Default format
  return format(date, 'yyyy-MM-dd');
}

/**
 * Adds days to a date
 * @param {Date} date - The base date
 * @param {number} days - Number of days to add
 * @returns {Date} - New date
 */
export { addDays };

/**
 * Calculates the difference in days between two dates
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {number} - Difference in days
 */
export function getDateDifference(date1, date2) {
  if (!date1 || !date2 || !isValid(date1) || !isValid(date2)) {
    return 0;
  }
  return differenceInDays(date1, date2);
}

/**
 * Generates a sequence of dates between start and end
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {number} intervalDays - Interval in days (default: 1)
 * @returns {Array<Date>} - Array of dates
 */
export function generateDateSequence(startDate, endDate, intervalDays = 1) {
  const dates = [];
  
  if (!startDate || !endDate || !isValid(startDate) || !isValid(endDate)) {
    return dates;
  }

  let currentDate = new Date(startDate);
  
  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate = addDays(currentDate, intervalDays);
  }

  return dates;
}

/**
 * Detects the date format from a string
 * @param {string} dateString - Example date string
 * @returns {string|null} - Detected format pattern or null
 */
export function detectDateFormat(dateString) {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  const patterns = [
    { regex: /^\d{4}-\d{2}-\d{2}$/, format: 'yyyy-MM-dd' },
    { regex: /^\d{2}\/\d{2}\/\d{4}$/, format: 'dd/MM/yyyy' },
    { regex: /^\d{2}-\d{2}-\d{4}$/, format: 'dd-MM-yyyy' },
    { regex: /^\d{4}\/\d{2}\/\d{2}$/, format: 'yyyy/MM/dd' },
    { regex: /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/, format: 'yyyy-MM-dd HH:mm:ss' },
    { regex: /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/, format: 'dd/MM/yyyy HH:mm:ss' },
    { regex: /^\d{4}-\d{2}$/, format: 'yyyy-MM' },
    { regex: /^\d{2}\/\d{4}$/, format: 'MM/yyyy' },
    { regex: /^[A-Za-z]{3} \d{4}$/, format: 'MMM yyyy' },
    { regex: /^[A-Za-z]+ \d{4}$/, format: 'MMMM yyyy' },
    { regex: /^\d{4}$/, format: 'yyyy' }
  ];

  for (const pattern of patterns) {
    if (pattern.regex.test(dateString.trim())) {
      return pattern.format;
    }
  }

  return null;
}

/**
 * Analyzes a series of dates to determine the frequency
 * @param {Array<Date>} dates - Array of dates
 * @returns {Object} - { frequency: 'daily'|'weekly'|'monthly'|'yearly'|'irregular', averageInterval: number }
 */
export function analyzeDateFrequency(dates) {
  if (!dates || dates.length < 2) {
    return { frequency: 'unknown', averageInterval: 0 };
  }

  // Sort dates
  const sortedDates = [...dates].sort((a, b) => a - b);
  
  // Calculate intervals
  const intervals = [];
  for (let i = 1; i < sortedDates.length; i++) {
    const diff = differenceInDays(sortedDates[i], sortedDates[i - 1]);
    if (diff > 0) {
      intervals.push(diff);
    }
  }

  if (intervals.length === 0) {
    return { frequency: 'unknown', averageInterval: 0 };
  }

  // Calculate average interval
  const avgInterval = intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length;

  // Determine frequency based on average interval
  let frequency = 'irregular';
  
  if (Math.abs(avgInterval - 1) < 0.1) {
    frequency = 'daily';
  } else if (Math.abs(avgInterval - 7) < 0.5) {
    frequency = 'weekly';
  } else if (Math.abs(avgInterval - 30) < 3) {
    frequency = 'monthly';
  } else if (Math.abs(avgInterval - 365) < 10) {
    frequency = 'yearly';
  }

  return { frequency, averageInterval: avgInterval };
}

/**
 * Generates future dates based on detected frequency
 * @param {Date} lastDate - The last date in the series
 * @param {string|Date} predictUntil - The date to predict until
 * @param {string} frequency - The detected frequency
 * @param {number} averageInterval - Average interval in days
 * @returns {Array<Date>} - Array of future dates
 */
export function generateFutureDatesWithFrequency(lastDate, predictUntil, frequency, averageInterval) {
  const futureDates = [];
  
  if (!lastDate || !isValid(lastDate)) {
    return futureDates;
  }

  // Parse predictUntil if it's a string
  const endDate = typeof predictUntil === 'string' ? parseDate(predictUntil) : predictUntil;
  
  // If no valid end date, generate a default number of points
  const maxDate = endDate && isValid(endDate) ? endDate : addDays(lastDate, 365); // Default to 1 year
  
  let currentDate = new Date(lastDate);
  let intervalDays = 1; // Default daily

  // Generate future dates with safety limit
  const MAX_FUTURE_DATES = 365; // Safety limit
  let count = 0;

  while (currentDate < maxDate && count < MAX_FUTURE_DATES) {
    // Handle different frequencies properly
    switch (frequency) {
      case 'daily':
        currentDate = addDays(currentDate, 1);
        break;
        
      case 'weekly':
        currentDate = addDays(currentDate, 7);
        break;
        
      case 'monthly':
        // Proper month addition (handles different month lengths)
        const nextMonth = new Date(currentDate);
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        // Keep the same day of month if possible
        if (nextMonth.getDate() !== currentDate.getDate()) {
          // Handle month overflow (e.g., Jan 31 -> Feb 28)
          nextMonth.setDate(0); // Last day of previous month
        }
        currentDate = nextMonth;
        break;
        
      case 'yearly':
        // Proper year addition
        const nextYear = new Date(currentDate);
        nextYear.setFullYear(nextYear.getFullYear() + 1);
        currentDate = nextYear;
        break;
        
      default:
        // Use calculated interval
        currentDate = addDays(currentDate, Math.round(averageInterval) || 1);
    }
    
    if (currentDate <= maxDate) {
      futureDates.push(new Date(currentDate));
      count++;
    }
  }

  if (count >= MAX_FUTURE_DATES) {
    console.warn(`Reached maximum prediction limit of ${MAX_FUTURE_DATES} dates`);
  }

  return futureDates;
}

/**
 * Converts a date to a numeric value for regression
 * @param {Date} date - The date to convert
 * @param {Date} baseDate - The base date for relative calculation
 * @returns {number} - Numeric representation
 */
export function dateToNumeric(date, baseDate) {
  if (!date || !baseDate || !isValid(date) || !isValid(baseDate)) {
    return 0;
  }
  
  // Return days since base date
  return differenceInDays(date, baseDate);
}

/**
 * Converts a numeric value back to a date
 * @param {number} numeric - The numeric value
 * @param {Date} baseDate - The base date
 * @returns {Date} - The calculated date
 */
export function numericToDate(numeric, baseDate) {
  if (!baseDate || !isValid(baseDate)) {
    return new Date();
  }
  
  return addDays(baseDate, Math.round(numeric));
}